import { useState } from 'react';
import { X, Check, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const CONFIDENCE_STYLES = {
  high: { label: 'Haute', dots: 3, className: 'text-green-600' },
  medium: { label: 'Moyenne', dots: 2, className: 'text-orange-500' },
  low: { label: 'Faible', dots: 1, className: 'text-red-500' },
};

function ConfidenceBadge({ level }) {
  const config = CONFIDENCE_STYLES[level] || CONFIDENCE_STYLES.medium;
  return (
    <span className={`flex items-center gap-1 text-xs ${config.className}`}>
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className={`inline-block w-1.5 h-1.5 rounded-full ${
            i <= config.dots ? 'bg-current' : 'bg-slate-200'
          }`}
        />
      ))}
      <span className="ml-0.5">{config.label}</span>
    </span>
  );
}

export default function DocumentAnalysisPanel({
  document,
  label,
  onValidate,
  onClose,
}) {
  const analysis = document.analyse_donnees_extraites;
  const fields = analysis?.fields || [];
  const summary = analysis?.summary || document.analyse_resume || '';

  // Chemins déjà injectés (snapshot stocké sur le document).
  const injectedPaths = new Set(
    (document.injected_fields || []).map((f) => f.path)
  );
  const injectedCount = injectedPaths.size;

  const [selectedFields, setSelectedFields] = useState(() =>
    new Set(
      fields
        .filter(
          (f) => f.confidence === 'high' && !injectedPaths.has(f.path),
        )
        .map((f) => f.path),
    ),
  );
  const [showSummary, setShowSummary] = useState(true);
  const [validating, setValidating] = useState(false);

  const toggleField = (path) => {
    if (injectedPaths.has(path)) return; // déjà injecté → inchangeable
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const selectAll = () =>
    setSelectedFields(
      new Set(
        fields
          .filter((f) => !injectedPaths.has(f.path))
          .map((f) => f.path),
      ),
    );
  const selectNone = () => setSelectedFields(new Set());

  const handleValidate = async () => {
    setValidating(true);
    try {
      await onValidate([...selectedFields]);
      onClose();
    } catch {
      setValidating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-lg bg-white shadow-xl flex flex-col animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900">Analyse IA</h2>
              {injectedCount > 0 && (
                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  ✓ {injectedCount} champ{injectedCount > 1 ? 's' : ''} injecté{injectedCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500">{label}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Error */}
          {document.analyse_erreur && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{document.analyse_erreur}</p>
            </div>
          )}

          {/* Summary */}
          {summary && (
            <section>
              <button
                type="button"
                onClick={() => setShowSummary(!showSummary)}
                className="flex items-center justify-between w-full text-left"
              >
                <h3 className="text-sm font-semibold text-slate-900">Résumé</h3>
                {showSummary ? (
                  <ChevronUp className="h-4 w-4 text-slate-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                )}
              </button>
              {showSummary && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg text-sm text-slate-700 whitespace-pre-wrap">
                  {summary}
                </div>
              )}
            </section>
          )}

          {/* Extracted fields */}
          {fields.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900">
                  Données extraites ({fields.length})
                </h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAll}
                    className="text-xs text-carex-600 hover:underline"
                  >
                    Tout sélectionner
                  </button>
                  <span className="text-xs text-slate-300">|</span>
                  <button
                    type="button"
                    onClick={selectNone}
                    className="text-xs text-slate-500 hover:underline"
                  >
                    Aucun
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {fields.map((field) => {
                  const isInjected = injectedPaths.has(field.path);
                  const isChecked = isInjected || selectedFields.has(field.path);
                  return (
                    <label
                      key={field.path}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                        isInjected
                          ? 'border-green-200 bg-green-50/50 cursor-not-allowed'
                          : isChecked
                            ? 'border-carex-300 bg-carex-50/50 cursor-pointer'
                            : 'border-slate-200 bg-white hover:border-slate-300 cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isInjected}
                        onChange={() => toggleField(field.path)}
                        className="mt-0.5 rounded border-slate-300 text-carex-600 focus:ring-carex-500 disabled:opacity-60"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-slate-900">{field.label}</span>
                          <div className="flex items-center gap-2">
                            {isInjected && (
                              <span className="text-xs text-green-700 font-medium">Injecté</span>
                            )}
                            <ConfidenceBadge level={field.confidence} />
                          </div>
                        </div>
                        <div className="mt-1 text-sm text-carex-700 font-medium">
                          {String(field.value)}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>
          )}

          {fields.length === 0 && !document.analyse_erreur && (
            <p className="text-sm text-slate-500 text-center py-4">
              Aucune donnée extractible pour ce type de document.
              {summary && ' Consultez le résumé ci-dessus.'}
            </p>
          )}

          {/* Already validated */}
          {document.analyse_validee && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700">
                Données validées et injectées dans le formulaire.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {fields.length > 0 && !document.analyse_validee && (
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={handleValidate}
              disabled={selectedFields.size === 0 || validating}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-carex-600 hover:bg-carex-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="h-4 w-4" />
              Appliquer {selectedFields.size} champ{selectedFields.size > 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
